import type {
  ApprovalRequest,
  AuditEvent,
  BranchAccount,
  DeliveryArea,
  DoneForYouSetupChannel,
  DoneForYouSetupRequest,
  DoneForYouSetupStatus,
  DiscrepancyRecord,
  DiscoveryRunRecord,
  DiscoverySource,
  ExternalInquiryRecord,
  ExternalSupplierRecord,
  InvoiceRecord,
  LocalizationRule,
  MarketCoverageRecord,
  MessageThread,
  NetworkOperatingSnapshot,
  Order,
  OrderItem,
  PriceBenchmarkRecord,
  Product,
  QuoteRequest,
  RestaurantSupplierContactRecord,
  Role,
  StockStatus,
  DemandGap,
  SupplierFulfillmentOrder,
  SupplierConfirmationTrustAction,
  SupplierDeliveryAvailability,
  SupplierConversionTask,
  SupplierOrderRequest,
  SupplierOrderRequestStatus,
  SupplierSearchDemand,
  SourcingSprint,
  WorkspaceUser,
} from "../types";
import type { ImportRejection, ImportRowMeta } from "./imports";
import { getAccessToken } from "./supabaseAuth";

// Every call to our Pages Functions goes through here so the signed-in user's
// Supabase access token rides along. The Functions verify it and read/write
// under Row-Level Security, which is what keeps one organization's data
// invisible to another. Without a token the request is rejected (401) rather
// than silently succeeding.
async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

export interface SessionAccount {
  organizationId: string;
  organizationName: string;
  type: string;
  role: Role;
  memberRole: string;
  fullName: string;
}

export type FetchSessionResult =
  | { ok: true; account: SessionAccount }
  | { ok: false; status: number; error: string };

/**
 * Ask the backend who the signed-in user is. 200 -> account; 403
 * "needs_onboarding" -> authenticated but no organization yet; 401 -> not
 * signed in. The frontend uses this to gate the workspace and to decide when to
 * run signup bootstrap.
 */
export async function fetchSession(): Promise<FetchSessionResult> {
  try {
    const response = await apiFetch("/api/session", {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (response.ok) {
      const payload = (await response.json()) as { ok: boolean; account: SessionAccount };
      return payload.ok
        ? { ok: true, account: payload.account }
        : { ok: false, status: response.status, error: "session_error" };
    }

    let error = `session_${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) {
        error = body.error;
      }
    } catch {
      // keep the status-based error
    }
    return { ok: false, status: response.status, error };
  } catch {
    return { ok: false, status: 0, error: "network_error" };
  }
}

export interface SubmitOrderInput {
  restaurantName: string;
  branchName?: string;
  supplierName: string;
  city: string;
  deliveryWindow: string;
  items: OrderItem[];
}

export type SubmitOrderResult =
  | {
      ok: true;
      mode: "supabase" | "local";
      orderNumber: string;
      total: number;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitInquiryInput {
  contactHint?: string;
  deliveryHint?: string;
  externalSupplierId: string;
  supplierName: string;
  buyerName: string;
  market?: string;
  productName: string;
  quantity: string;
  neededBy: string;
  contactDetails: string;
  messageBody: string;
  source?: string;
}

export type SubmitInquiryResult =
  | {
      ok: true;
      mode: "supabase";
      inquiry: {
        id: string;
        supplierName: string;
        productName: string;
        messageBody: string;
      };
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type UpdateInquiryStatusResult =
  | {
      ok: true;
      mode: "supabase";
      inquiry: {
        id: string;
        status: string;
      };
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitQuoteRequestInput {
  buyer: string;
  market?: string;
  product: string;
  quantity: string;
  neededBy: string;
  suppliersMatched: number;
  budget: string;
  supplierName?: string;
  category?: string;
  deliveryWindow?: string;
  priceRange?: string;
  unitPrice?: number;
}

export type SubmitQuoteRequestResult =
  | {
      ok: true;
      mode: "supabase";
      quoteRequest: {
        id: string;
        buyer: string;
        product: string;
        quantity: string;
        neededBy: string;
        status: "draft" | "sent" | "quoted" | "waiting" | "accepted" | "closed";
        suppliersMatched: number;
        budget: string;
        supplierName?: string;
        category?: string;
        deliveryWindow?: string;
        priceRange?: string;
        unitPrice?: number;
        quotedAt?: string;
        termsNote?: string;
      };
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface AcceptQuoteOfferInput {
  quoteRequestId: string;
  supplierName?: string;
}

export type AcceptQuoteOfferResult =
  | {
      ok: true;
      mode: "supabase";
      quoteRequest: QuoteRequest;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitQuoteResponseInput {
  buyerName: string;
  currency?: string;
  discountPercent?: number;
  deliveryWindow?: string;
  listPrice?: number;
  market?: string;
  offerType?: "private_quote" | "competitive_discount" | "usual_supplier" | "backup_offer";
  priceRange?: string;
  productName: string;
  quantity: string;
  quoteRequestId: string;
  quotedPrice?: number;
  savingsAmount?: number;
  supplierName: string;
  termsNote?: string;
}

export type SubmitQuoteResponseResult =
  | {
      ok: true;
      mode: "supabase";
      quoteResponse: {
        quoteRequestId: string;
        requestNumber?: string;
        supplierId: string;
        supplierName: string;
        status: "quoted";
        quotedPrice: number | null;
        currency: string;
        discountPercent?: number | null;
        deliveryWindow?: string;
        listPrice?: number | null;
        offerType?: string;
        savingsAmount?: number | null;
        termsNote?: string;
      };
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitImportBatchInput {
  fileName: string;
  importType: "supplier_catalog" | "restaurant_order_guide";
  market?: string;
  organizationName: string;
  role: Role;
  acceptedRows: Array<(Product | OrderItem) & ImportRowMeta>;
  rejectedRows: ImportRejection[];
  rowCount: number;
}

export type SubmitImportBatchResult =
  | {
      ok: true;
      mode: "supabase";
      batchId: string;
      acceptedCount: number;
      rejectedCount: number;
      catalogProductsSaved?: number;
      orderGuideItemsSaved?: number;
      products?: Product[];
      orderGuideItems?: OrderItem[];
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitIntegrationRequestInput {
  organizationName: string;
  role: Role;
  providerName: string;
  systemType: string;
  syncTarget: string;
  endpointUrl: string;
  authType: string;
}

export type SubmitIntegrationRequestResult =
  | {
      ok: true;
      mode: "supabase";
      connectorRequest: BootstrapConnectorRequest;
      requestId: string;
      status: BootstrapConnectorRequest["status"];
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitDoneForYouSetupRequestInput {
  branchName: string;
  city: string;
  contactName: string;
  contactChannel: DoneForYouSetupChannel;
  contactValue: string;
  supplierListText: string;
  orderHabitsText: string;
  preferredSetupWindow: string;
}

export interface UpdateDoneForYouSetupRequestInput {
  requestId: string;
  status: DoneForYouSetupStatus;
  operatorNote?: string;
}

export interface ConvertDoneForYouSetupRequestInput {
  requestId: string;
  supplierContacts: Array<{
    category: string;
    contactPerson?: string;
    contactValue: string;
    deliveryArea: string;
    email?: string;
    name: string;
    phone?: string;
    preferredChannel: "whatsapp" | "viber" | "sms" | "email" | "copy";
    usualDays: string;
    usualOrderText?: string;
    viber?: string;
    whatsapp?: string;
  }>;
  orderItems: Array<{
    category?: string;
    name: string;
    note?: string;
    quantity: number;
    supplier?: string;
    unit: string;
    unitPrice: number;
  }>;
}

export type SubmitDoneForYouSetupRequestResult =
  | {
      ok: true;
      mode: "supabase";
      contactsCreated?: number;
      orderGuideItemsCreated?: number;
      request: DoneForYouSetupRequest;
      requestId: string;
      status: DoneForYouSetupStatus;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface UpdateIntegrationConnectionInput {
  requestId: string;
  status: "pending_setup" | "connected" | "failed";
  statusNote: string;
}

export interface SubmitDiscoverySourceRunInput {
  sourceId: string;
}

export type SubmitDiscoverySourceRunResult =
  | {
      ok: true;
      mode: "supabase";
      source: DiscoverySource;
      run: DiscoveryRunRecord;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitPreferredSupplierInput {
  action: "save" | "remove";
  branchId: string;
  category?: string;
  market?: string;
  supplierName: string;
  supplierType: "verified" | "external";
}

export type SubmitPreferredSupplierResult =
  | {
      ok: true;
      mode: "supabase";
      preference: {
        id: string;
        supplierName: string;
        status: "active" | "removed";
      };
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitPublicSupplierSourceInput {
  sourceUrl: string;
  market: string;
  sourceType: string;
}

export type SubmitPublicSupplierSourceResult =
  | {
      ok: true;
      mode: "supabase";
      contentType: string;
      conversionTask?: SupplierConversionTask;
      record: ExternalSupplierRecord;
    }
  | {
      ok: false;
      error: string;
      status?: number;
    };

export interface BootstrapImportRun {
  id: string;
  batchId: string;
  fileName: string;
  importType: "supplier_catalog" | "restaurant_order_guide";
  organizationName: string;
  role: Role;
  mode: "supabase";
  rowCount: number;
  acceptedCount: number;
  rejectedCount: number;
  rejectedRows: ImportRejection[];
  createdAt: string;
}

export type BootstrapConnectorRequest = SubmitIntegrationRequestInput & {
  id: string;
  requestId: string;
  mode: "supabase";
  status: "pending_setup" | "connected" | "failed";
  statusNote?: string;
  createdAt: string;
  updatedAt?: string;
};

export type FetchAppBootstrapResult =
  | {
      ok: true;
      mode: "supabase";
      loadedAt: string;
      data: {
        approvalRequests: ApprovalRequest[];
        auditEvents: AuditEvent[];
        branchAccounts: BranchAccount[];
        connectorRequests: BootstrapConnectorRequest[];
        doneForYouSetupRequests: DoneForYouSetupRequest[];
        deliveryAreas: DeliveryArea[];
        discrepancies: DiscrepancyRecord[];
        discoveryRuns: DiscoveryRunRecord[];
        discoverySources: DiscoverySource[];
        externalInquiries: ExternalInquiryRecord[];
        externalSuppliers: ExternalSupplierRecord[];
        fulfillmentOrders: SupplierFulfillmentOrder[];
        importRuns: BootstrapImportRun[];
        invoices: InvoiceRecord[];
        demandGaps: DemandGap[];
        localizationRules: LocalizationRule[];
        marketCoverageRecords: MarketCoverageRecord[];
        orderGuideItems: OrderItem[];
        sourcingSprints: SourcingSprint[];
        supplierConversionTasks: SupplierConversionTask[];
        supplierSearchDemand: SupplierSearchDemand[];
        orders: Order[];
        priceBenchmarks: PriceBenchmarkRecord[];
        preferredSupplierNames: string[];
        products: Product[];
        quoteRequests: QuoteRequest[];
        restaurantSupplierContacts: RestaurantSupplierContactRecord[];
        messageThreads: MessageThread[];
        networkSnapshot: NetworkOperatingSnapshot;
        supplierOrderRequests: SupplierOrderRequest[];
        workspaceUsers: WorkspaceUser[];
      };
      counts: Record<string, number>;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function fetchAppBootstrap(): Promise<FetchAppBootstrapResult> {
  try {
    const response = await apiFetch("/api/bootstrap", {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    const payload = (await response.json()) as FetchAppBootstrapResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Bootstrap API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "bootstrap_not_loaded",
    };
  }
}

export type FetchPriceBenchmarksResult =
  | {
      ok: true;
      mode: "supabase";
      priceBenchmarks: PriceBenchmarkRecord[];
      privacy: {
        source: "restaurant_reported";
        minimumSamples: number;
        minimumRestaurants: number;
        rawSupplierDealsExposed: boolean;
      };
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function fetchPriceBenchmarks(city?: string): Promise<FetchPriceBenchmarksResult> {
  const query = city ? `?city=${encodeURIComponent(city)}` : "";

  try {
    const response = await apiFetch(`/api/price-benchmarks${query}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    const payload = (await response.json()) as FetchPriceBenchmarksResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Price benchmark API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "price_benchmarks_not_loaded",
    };
  }
}

export type SubmitRestaurantSupplierContactResult =
  | {
      ok: true;
      mode: "supabase";
      contact: RestaurantSupplierContactRecord;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function submitRestaurantSupplierContact(
  contact: RestaurantSupplierContactRecord,
): Promise<SubmitRestaurantSupplierContactResult> {
  try {
    const response = await apiFetch("/api/supplier-contacts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(contact),
    });
    const payload = (await response.json()) as SubmitRestaurantSupplierContactResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Supplier contact API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "supplier_contact_not_saved",
    };
  }
}

export type SubmitSupplierOrderRequestResult =
  | {
      ok: true;
      mode: "supabase";
      request: SupplierOrderRequest;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function submitSupplierOrderRequest(
  request: SupplierOrderRequest,
): Promise<SubmitSupplierOrderRequestResult> {
  try {
    const response = await apiFetch("/api/supplier-order-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
    const payload = (await response.json()) as SubmitSupplierOrderRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Supplier order request API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "supplier_order_request_not_saved",
    };
  }
}

export type SupplierOrderConfirmationResult =
  | {
      ok: true;
      mode: "supabase";
      request: SupplierOrderRequest;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function fetchSupplierOrderConfirmation(token: string): Promise<SupplierOrderConfirmationResult> {
  try {
    const response = await fetch(`/api/supplier-order-confirmations?token=${encodeURIComponent(token)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    const payload = (await response.json()) as SupplierOrderConfirmationResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Supplier confirmation API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "supplier_confirmation_not_loaded",
    };
  }
}

export interface RespondToSupplierOrderConfirmationInput {
  deliveryAvailability?: SupplierDeliveryAvailability;
  deliveryWindow?: string;
  items: OrderItem[];
  note: string;
  status: SupplierOrderRequestStatus;
  token: string;
  trustAction?: SupplierConfirmationTrustAction;
}

export async function respondToSupplierOrderConfirmation(
  input: RespondToSupplierOrderConfirmationInput,
): Promise<SupplierOrderConfirmationResult> {
  try {
    const response = await fetch("/api/supplier-order-confirmations", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SupplierOrderConfirmationResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Supplier confirmation API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "supplier_confirmation_not_saved",
    };
  }
}

export interface SubmitCatalogProductInput {
  category: string;
  currency: string;
  minimumOrderQuantity: number;
  name: string;
  price: number;
  productId?: string;
  stockStatus: StockStatus;
  supplierName: string;
  unit: string;
}

export type SubmitCatalogProductResult =
  | {
      ok: true;
      mode: "supabase";
      product: Product;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitDeliveryAreaInput {
  activeOrders?: number;
  areaId?: string;
  days: string;
  market: string;
  minimumOrder: string;
  performance: number;
  status: DeliveryArea["status"];
  supplierName: string;
}

export type SubmitDeliveryAreaResult =
  | {
      ok: true;
      mode: "supabase";
      deliveryArea: DeliveryArea;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitFulfillmentUpdateInput {
  amountEur?: number;
  buyerName: string;
  market?: string;
  orderId: string;
  status: SupplierFulfillmentOrder["status"];
  supplierName?: string;
}

export type SubmitFulfillmentUpdateResult =
  | {
      ok: true;
      mode: "supabase";
      fulfillment: {
        orderId: string;
        status: SupplierFulfillmentOrder["status"];
        orderStatus: Order["status"];
      };
      invoice?: InvoiceRecord;
      discrepancyId?: string;
      resolvedDiscrepancies?: number;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitInvoiceInput {
  amountBam: number;
  buyerName?: string;
  captureMethod?: InvoiceRecord["captureMethod"];
  due: string;
  fileUrl?: string;
  lineItems?: InvoiceRecord["invoiceLines"];
  mismatchTypes?: InvoiceRecord["mismatchTypes"];
  orderId?: string;
  supplierName: string;
}

export type SubmitInvoiceResult =
  | {
      ok: true;
      mode: "supabase";
      invoice: InvoiceRecord;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface UpdateInvoiceStatusInput {
  invoiceId: string;
  status: "ready" | "exported" | "paid" | "blocked";
}

export type UpdateInvoiceStatusResult =
  | {
      ok: true;
      mode: "supabase";
      invoice: InvoiceRecord;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function submitCatalogProduct(
  input: SubmitCatalogProductInput,
): Promise<SubmitCatalogProductResult> {
  try {
    const response = await apiFetch("/api/catalog-products", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitCatalogProductResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Catalog API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "catalog_product_not_saved",
    };
  }
}

export async function submitDeliveryArea(
  input: SubmitDeliveryAreaInput,
): Promise<SubmitDeliveryAreaResult> {
  try {
    const response = await apiFetch("/api/delivery-areas", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitDeliveryAreaResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Delivery area API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "delivery_area_not_saved",
    };
  }
}

export async function submitFulfillmentUpdate(
  input: SubmitFulfillmentUpdateInput,
): Promise<SubmitFulfillmentUpdateResult> {
  try {
    const response = await apiFetch("/api/fulfillment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitFulfillmentUpdateResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Fulfillment API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "fulfillment_not_saved",
    };
  }
}

export async function submitInvoice(input: SubmitInvoiceInput): Promise<SubmitInvoiceResult> {
  try {
    const response = await apiFetch("/api/invoices", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitInvoiceResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Invoice API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "invoice_not_saved",
    };
  }
}

export async function updateInvoiceStatus(
  input: UpdateInvoiceStatusInput,
): Promise<UpdateInvoiceStatusResult> {
  try {
    const response = await apiFetch("/api/invoices", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as UpdateInvoiceStatusResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Invoice API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "invoice_not_saved",
    };
  }
}

export interface SubmitBranchBudgetInput {
  approvalLimit: number;
  branchId?: string;
  branchName: string;
  market: string;
  organizationName?: string;
  role: Role;
  weeklyBudget: number;
}

export type SubmitBranchBudgetResult =
  | {
      ok: true;
      mode: "supabase";
      branch: Pick<BranchAccount, "id" | "name" | "market" | "weeklyBudget" | "approvalLimit">;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export interface SubmitWorkspaceUserInput {
  branchId?: string;
  branchName: string;
  email: string;
  focus: string;
  lastSeen: string;
  market: string;
  name: string;
  organizationName?: string;
  permissionRoleId: string;
  role: Role;
  status: WorkspaceUser["status"];
}

export type SubmitWorkspaceUserResult =
  | {
      ok: true;
      mode: "supabase";
      user: WorkspaceUser;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitApprovalRequestInput = ApprovalRequest & {
  branchId?: string;
  market?: string;
  organizationName?: string;
};

export type SubmitApprovalRequestResult =
  | {
      ok: true;
      mode: "supabase";
      approval: ApprovalRequest;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitMessageThreadResult =
  | {
      ok: true;
      mode: "supabase";
      message: MessageThread;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitAuditEventInput = Omit<AuditEvent, "id" | "createdAt">;

export type SubmitAuditEventResult =
  | {
      ok: true;
      mode: "supabase";
      auditEvent: AuditEvent;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function submitBranchBudget(
  input: SubmitBranchBudgetInput,
): Promise<SubmitBranchBudgetResult> {
  try {
    const response = await apiFetch("/api/workspace", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, action: "branch_budget" }),
    });
    const payload = (await response.json()) as SubmitBranchBudgetResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Workspace API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "branch_budget_not_saved",
    };
  }
}

export async function submitWorkspaceUser(
  input: SubmitWorkspaceUserInput,
): Promise<SubmitWorkspaceUserResult> {
  try {
    const response = await apiFetch("/api/workspace", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, action: "workspace_user" }),
    });
    const payload = (await response.json()) as SubmitWorkspaceUserResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Workspace API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "workspace_user_not_saved",
    };
  }
}

export async function submitApprovalRequest(
  input: SubmitApprovalRequestInput,
): Promise<SubmitApprovalRequestResult> {
  try {
    const response = await apiFetch("/api/approval-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitApprovalRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Approval API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "approval_not_saved",
    };
  }
}

export async function submitMessageThread(thread: MessageThread): Promise<SubmitMessageThreadResult> {
  try {
    const response = await apiFetch("/api/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(thread),
    });
    const payload = (await response.json()) as SubmitMessageThreadResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Message API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "message_not_saved",
    };
  }
}

export async function submitAuditEvent(input: SubmitAuditEventInput): Promise<SubmitAuditEventResult> {
  try {
    const response = await apiFetch("/api/audit-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitAuditEventResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Audit API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "audit_event_not_saved",
    };
  }
}

export type SubmitDemandGapResult =
  | {
      ok: true;
      mode: "supabase";
      demandGap: DemandGap;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitSupplierDemandResult =
  | {
      ok: true;
      mode: "supabase";
      supplierDemand: SupplierSearchDemand;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitCoverageRecordResult =
  | {
      ok: true;
      mode: "supabase";
      coverageRecord: MarketCoverageRecord;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitSourcingSprintResult =
  | {
      ok: true;
      mode: "supabase";
      sourcingSprint: SourcingSprint;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export type SubmitLocalizationRuleResult =
  | {
      ok: true;
      mode: "supabase";
      localizationRule: LocalizationRule;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

async function submitNetworkIntelligence<T>(
  body: Record<string, unknown>,
  fallbackError: string,
): Promise<T> {
  try {
    const response = await apiFetch("/api/network-intelligence", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const payload = (await response.json()) as T & { ok: boolean; error?: string };

    if (!response.ok || !payload.ok) {
      return (
        payload.ok
          ? {
              ok: false,
              mode: "supabase",
              error: `Network intelligence API failed with ${response.status}`,
            }
          : payload
      ) as T;
    }

    return payload as T;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: fallbackError,
    } as T;
  }
}

export function submitDemandGap(demandGap: DemandGap): Promise<SubmitDemandGapResult> {
  return submitNetworkIntelligence<SubmitDemandGapResult>(
    { action: "demand_gap", demandGap },
    "demand_gap_not_saved",
  );
}

export function submitSupplierDemand(
  supplierDemand: SupplierSearchDemand,
): Promise<SubmitSupplierDemandResult> {
  return submitNetworkIntelligence<SubmitSupplierDemandResult>(
    { action: "supplier_demand", supplierDemand },
    "supplier_demand_not_saved",
  );
}

export function submitCoverageRecord(
  coverageRecord: MarketCoverageRecord,
): Promise<SubmitCoverageRecordResult> {
  return submitNetworkIntelligence<SubmitCoverageRecordResult>(
    { action: "coverage_record", coverageRecord },
    "coverage_not_saved",
  );
}

export function submitSourcingSprint(
  sourcingSprint: SourcingSprint,
): Promise<SubmitSourcingSprintResult> {
  return submitNetworkIntelligence<SubmitSourcingSprintResult>(
    { action: "sourcing_sprint", sourcingSprint },
    "sprint_not_saved",
  );
}

export function submitLocalizationRule(
  localizationRule: LocalizationRule,
): Promise<SubmitLocalizationRuleResult> {
  return submitNetworkIntelligence<SubmitLocalizationRuleResult>(
    { action: "localization_rule", localizationRule },
    "localization_not_saved",
  );
}

export async function submitOrder(input: SubmitOrderInput): Promise<SubmitOrderResult> {
  const total = input.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  try {
    const response = await apiFetch("/api/orders", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, total }),
    });
    const payload = (await response.json()) as SubmitOrderResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Order API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "order_not_saved",
    };
  }
}

export async function submitInquiry(input: SubmitInquiryInput): Promise<SubmitInquiryResult> {
  try {
    const response = await apiFetch("/api/inquiries", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitInquiryResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Inquiry API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "inquiry_not_saved",
    };
  }
}

export async function updateInquiryStatus(
  inquiryId: string,
  status: "draft" | "sent" | "replied" | "converted" | "closed",
): Promise<UpdateInquiryStatusResult> {
  try {
    const response = await apiFetch("/api/inquiries", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ inquiryId, status }),
    });
    const payload = (await response.json()) as UpdateInquiryStatusResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Inquiry status API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "inquiry_status_not_saved",
    };
  }
}

export async function submitQuoteRequest(
  input: SubmitQuoteRequestInput,
): Promise<SubmitQuoteRequestResult> {
  try {
    const response = await apiFetch("/api/quote-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitQuoteRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Quote request API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "quote_request_not_saved",
    };
  }
}

export async function acceptQuoteOffer(input: AcceptQuoteOfferInput): Promise<AcceptQuoteOfferResult> {
  try {
    const response = await apiFetch("/api/quote-requests", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as AcceptQuoteOfferResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Quote acceptance API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "quote_acceptance_not_saved",
    };
  }
}

export async function submitQuoteResponse(
  input: SubmitQuoteResponseInput,
): Promise<SubmitQuoteResponseResult> {
  try {
    const response = await apiFetch("/api/quote-responses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitQuoteResponseResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Quote response API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "quote_response_not_saved",
    };
  }
}

export async function submitImportBatch(input: SubmitImportBatchInput): Promise<SubmitImportBatchResult> {
  try {
    const response = await apiFetch("/api/imports", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitImportBatchResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Import API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "import_not_saved",
    };
  }
}

export async function submitIntegrationRequest(
  input: SubmitIntegrationRequestInput,
): Promise<SubmitIntegrationRequestResult> {
  try {
    const response = await apiFetch("/api/integration-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitIntegrationRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Integration request API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "integration_request_not_saved",
    };
  }
}

export async function submitDoneForYouSetupRequest(
  input: SubmitDoneForYouSetupRequestInput,
): Promise<SubmitDoneForYouSetupRequestResult> {
  try {
    const response = await apiFetch("/api/done-for-you-setup-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitDoneForYouSetupRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Done-for-you setup API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "done_for_you_setup_not_saved",
    };
  }
}

export async function updateDoneForYouSetupRequest(
  input: UpdateDoneForYouSetupRequestInput,
): Promise<SubmitDoneForYouSetupRequestResult> {
  try {
    const response = await apiFetch("/api/done-for-you-setup-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitDoneForYouSetupRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Done-for-you setup update failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "done_for_you_setup_not_saved",
    };
  }
}

export async function convertDoneForYouSetupRequest(
  input: ConvertDoneForYouSetupRequestInput,
): Promise<SubmitDoneForYouSetupRequestResult> {
  try {
    const response = await apiFetch("/api/done-for-you-setup-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, action: "convert" }),
    });
    const payload = (await response.json()) as SubmitDoneForYouSetupRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Done-for-you setup conversion failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "done_for_you_setup_not_saved",
    };
  }
}

export async function updateIntegrationConnection(
  input: UpdateIntegrationConnectionInput,
): Promise<SubmitIntegrationRequestResult> {
  try {
    const response = await apiFetch("/api/integration-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitIntegrationRequestResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Integration update API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "integration_request_not_saved",
    };
  }
}

export async function submitDiscoverySourceRun(
  input: SubmitDiscoverySourceRunInput,
): Promise<SubmitDiscoverySourceRunResult> {
  try {
    const response = await apiFetch("/api/discovery-sources", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitDiscoverySourceRunResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Discovery source API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "discovery_run_not_saved",
    };
  }
}

export async function submitPreferredSupplier(
  input: SubmitPreferredSupplierInput,
): Promise<SubmitPreferredSupplierResult> {
  try {
    const response = await apiFetch("/api/preferred-suppliers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitPreferredSupplierResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `Preferred supplier API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "preferred_supplier_not_saved",
    };
  }
}

export async function submitPublicSupplierSource(
  input: SubmitPublicSupplierSourceInput,
): Promise<SubmitPublicSupplierSourceResult> {
  try {
    const response = await apiFetch("/api/public-supplier-source", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitPublicSupplierSourceResult;

    if (!response.ok || !payload.ok) {
      return {
        ok: false,
        error: payload.ok ? "source_fetch_failed" : payload.error,
        status: response.status,
      };
    }

    return payload;
  } catch {
    return {
      ok: false,
      error: "source_fetch_failed",
    };
  }
}

export interface SubmitExternalSupplierStatusInput {
  id: string;
  nextStep?: string;
  owner?: string;
  revenuePotentialEur?: number;
  status: ExternalSupplierRecord["status"];
}

export type SubmitExternalSupplierStatusResult =
  | {
      ok: true;
      mode: "supabase";
      record: ExternalSupplierRecord;
      conversionTask?: SupplierConversionTask;
    }
  | {
      ok: false;
      mode: "supabase" | "local";
      error: string;
    };

export async function submitExternalSupplierStatus(
  input: SubmitExternalSupplierStatusInput,
): Promise<SubmitExternalSupplierStatusResult> {
  try {
    const response = await apiFetch("/api/external-suppliers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as SubmitExternalSupplierStatusResult;

    if (!response.ok || !payload.ok) {
      return payload.ok
        ? {
            ok: false,
            mode: "supabase",
            error: `External supplier API failed with ${response.status}`,
          }
        : payload;
    }

    return payload;
  } catch {
    return {
      ok: false,
      mode: "local",
      error: "external_supplier_not_saved",
    };
  }
}
